import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useNavigate } from 'react-router-dom';
import { Play, Lock, X, Clapperboard, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Videos({ user }) {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    axios.get(`${API}/videos`, { withCredentials: true })
      .then((r) => setVideos(r.data.videos || []))
      .catch((e) => { if (e.response?.status === 402) setLocked(true); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="hg-glass rounded-3xl p-6 md:p-7 relative overflow-hidden" data-testid="videos-hero">
          <div className="absolute -top-16 -right-12 w-56 h-56 rounded-full bg-[#C66B4A]/15 blur-3xl" />
          <div className="relative">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#5C665C]">Members only</p>
            <h1 className="text-3xl md:text-4xl font-bold text-[#1A4331] tracking-tight mt-1 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Clapperboard className="w-7 h-7 text-[#C66B4A]" /> Founder&apos;s Video Archive
            </h1>
            <p className="text-[#5C665C] mt-2 max-w-xl">Fireside catch-ups and seasonal masterclasses from the founders — watch on the big screen.</p>
          </div>
        </div>

        {locked ? (
          <div className="bg-[#C9A85B]/10 border border-[#C9A85B]/30 rounded-3xl p-8 text-center" data-testid="videos-locked">
            <Lock className="w-10 h-10 text-[#C9A85B] mx-auto mb-3" />
            <p className="text-lg font-semibold text-[#1A4331]">The Video Archive is a Premium feature</p>
            <p className="text-sm text-[#7A6B57] mt-1 mb-4">Upgrade to watch founder digests and masterclasses.</p>
            <button onClick={() => navigate('/subscription')} data-testid="videos-upgrade" className="bg-[#1A4331] text-[#FAF6EC] font-semibold px-6 py-3 rounded-full">Upgrade to Premium</button>
          </div>
        ) : loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-56 rounded-3xl bg-white/60 animate-pulse" />)}</div>
        ) : videos.length === 0 ? (
          <p className="text-center text-[#5C665C] py-12">No videos published yet — check back soon.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="videos-grid">
            {videos.map((v) => (
              <button key={v.id} onClick={() => setPlaying(v)} data-testid={`video-${v.id}`}
                className="hg-press text-left rounded-3xl overflow-hidden bg-white shadow-[0_14px_40px_-18px_rgba(26,67,49,0.4)] border border-[#1A4331]/5 group">
                <div className="relative h-44 overflow-hidden bg-[#0F2D1F]">
                  {v.thumbnail_url && <img src={v.thumbnail_url} alt={v.title} loading="lazy" className="w-full h-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105" />}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg"><Play className="w-6 h-6 text-[#1A4331] fill-[#1A4331] ml-0.5" /></div>
                  </div>
                  {v.duration && <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs font-semibold px-2 py-0.5 rounded">{v.duration}</span>}
                </div>
                <div className="p-4">
                  <span className="text-[11px] font-semibold text-[#C66B4A] uppercase tracking-wide">{v.category}</span>
                  <h3 className="text-lg font-bold text-[#1A4331] leading-tight mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{v.title}</h3>
                  {v.description && <p className="text-sm text-[#5C665C] mt-1 line-clamp-2 leading-relaxed">{v.description}</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {playing && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPlaying(null)} data-testid="video-player">
          <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-white">
                <Sparkles className="w-4 h-4 text-[#C9A85B]" />
                <span className="font-semibold">{playing.title}</span>
              </div>
              <button onClick={() => setPlaying(null)} data-testid="video-close" className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X className="w-5 h-5 text-white" /></button>
            </div>
            <video src={playing.video_url} controls autoPlay playsInline className="w-full rounded-2xl bg-black" style={{ maxHeight: '70vh' }} />
          </div>
        </div>
      )}
    </Layout>
  );
}
